'use client';

/**
 * 段级列配置弹窗
 *
 * 模式：
 *  - 单栏（默认）：列数 = 0，议程项用单 description 字段
 *  - 多栏：列数 ≥ 1，议程项的 columnDescriptions[] 与列名数组等长
 *
 * 快捷预设：单栏 / FFAI vs Robotics 双栏 / 三栏空白 / 自定义任意数量
 */

import { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useTranslation } from '@/hooks/useTranslation';
import { agendaApi, type MeetingAgendaSection } from '@/services/api/agenda';

interface Props {
  meetingId: string;
  section: MeetingAgendaSection;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  /** 保存后回调，传出更新后的 section（便于父组件局部 setState，避免全表 reload） */
  onSaved: (next: MeetingAgendaSection) => void;
}

export function SectionColumnsDialog({ meetingId, section, open, onOpenChange, onSaved }: Props) {
  const { t } = useTranslation();
  const tag = t.meetingAttendance.agenda;
  const cl = tag.columnConfig;

  const [labels, setLabels] = useState<string[]>(section.columnLabels);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setLabels(section.columnLabels);
  }, [open, section.columnLabels]);

  const applyPreset = (preset: 'single' | 'ffai-robotics' | 'three' | 'custom') => {
    if (preset === 'single') setLabels([]);
    else if (preset === 'ffai-robotics') setLabels(['FFAI/EAI-EV', 'EAI-Robotics']);
    else if (preset === 'three') setLabels(['', '', '']);
    else setLabels(labels.length > 0 ? labels : ['']);
  };

  const handleSave = async () => {
    const trimmed = labels.map((s) => s.trim());
    if (trimmed.length > 0 && trimmed.some((s) => !s)) {
      toast.error(cl.errorEmptyLabel);
      return;
    }
    setSaving(true);
    try {
      const resp = await agendaApi.updateAgendaSection(meetingId, section.id, {
        columnLabels: trimmed,
      });
      toast.success(tag.saveSuccess);
      onSaved(resp.section);
      onOpenChange(false);
    } catch {
      toast.error(tag.saveFailed);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{cl.dialogTitle}</DialogTitle>
          <DialogDescription>{cl.dialogDescription}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* 快捷预设 */}
          <div>
            <Label className="text-xs text-gray-600">{cl.presetLabel}</Label>
            <div className="mt-1 flex flex-wrap gap-2">
              <Button
                variant={labels.length === 0 ? 'default' : 'outline'}
                size="sm"
                onClick={() => applyPreset('single')}
              >
                {cl.presetSingle}
              </Button>
              <Button
                variant={
                  labels.length === 2 &&
                  labels[0] === 'FFAI/EAI-EV' &&
                  labels[1] === 'EAI-Robotics'
                    ? 'default'
                    : 'outline'
                }
                size="sm"
                onClick={() => applyPreset('ffai-robotics')}
              >
                {cl.presetFfaiRobotics}
              </Button>
              <Button
                variant={labels.length === 3 && labels.every((s) => !s) ? 'default' : 'outline'}
                size="sm"
                onClick={() => applyPreset('three')}
              >
                {cl.presetThree}
              </Button>
              <Button variant="outline" size="sm" onClick={() => applyPreset('custom')}>
                {cl.presetCustom}
              </Button>
            </div>
          </div>

          {/* 列名编辑 */}
          {labels.length > 0 && (
            <div>
              <Label className="text-xs text-gray-600">{cl.columnsListLabel}</Label>
              <div className="mt-1 space-y-2">
                {labels.map((label, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="w-6 text-xs text-gray-400">#{i + 1}</span>
                    <Input
                      value={label}
                      onChange={(e) => {
                        const next = [...labels];
                        next[i] = e.target.value;
                        setLabels(next);
                      }}
                      placeholder={cl.columnNamePlaceholder}
                      maxLength={100}
                      className="flex-1"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setLabels(labels.filter((_, j) => j !== i))}
                      title={cl.removeColumn}
                    >
                      <Trash2 className="h-3 w-3 text-red-500" />
                    </Button>
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => setLabels([...labels, ''])}
                >
                  <Plus className="mr-1 h-3 w-3" />
                  {cl.addColumn}
                </Button>
              </div>
            </div>
          )}

          {labels.length === 0 && (
            <div className="rounded border border-blue-100 bg-blue-50 p-3 text-xs text-blue-800">
              {cl.singleColumnHint}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            {t.meetingAttendance.common.back}
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {tag.saveButton}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
